import React from 'react';
import { Link, useNavigate } from 'react-router-dom';

const API = 'http://localhost:3001';
function withAuthHeaders(headers = {}) {
  const token = localStorage.getItem('authToken') || '';
  return token ? { ...headers, Authorization: `Bearer ${token}` } : headers;
}
async function fetchAuth(url, options = {}) {
  const opts = { ...options, headers: withAuthHeaders(options.headers || {}) };
  return fetch(url, opts);
}

function monthName(m) {
  const names = ['янв','фев','мар','апр','май','июн','июл','авг','сен','окт','ноя','дек'];
  const i = Math.max(1, Math.min(12, Number(m||0))) - 1;
  return names[i] || String(m);
}

function getRequestStatus(row) {
  let st = String(row.status || '').trim();
  if (!st) {
    if (row.gdSignedAt) st = 'done';
    else if (row.gdApprovalAt) st = 'awaiting_gd';
    else if (row.assistantSignedAt) st = 'done';
    else if (row.managerSignedAt) st = 'in_progress';
    else st = 'draft';
  }
  const map = {
    draft: { text: 'Черновик', badge: { bg: '#f3f4f6', color:'#374151', border:'#d1d5db' } },
    in_progress: { text: 'В обработке', badge: { bg: '#e0f2fe', color:'#0369a1', border:'#7dd3fc' } },
    awaiting_gd: { text: 'На утверждении у ГД', badge: { bg: '#fef9c3', color:'#b45309', border:'#fde68a' } },
    done: { text: 'Выполнено', badge: { bg: '#dcfce7', color:'#065f46', border:'#86efac' } },
  };
  const current = map[st] || map.draft;
  return { key: st, text: current.text, ...current.badge };
}

const linkStyle = {
  color: '#1d4ed8',
  textDecoration: 'underline',
  fontWeight: 600,
};

function StatusBadge({ row }) {
  const m = getRequestStatus(row);
  return (
    <span title={`Статус: ${m.text}`} style={{
      display:'inline-flex', alignItems:'center',
      padding:'4px 10px', fontSize:12, fontWeight:600,
      border:`1px solid ${m.border}`, borderRadius:16,
      background:m.bg, color:m.color, lineHeight:1
    }}>{m.text}</span>
  );
}

export default function TmcRequestsPage() {
  const nav = useNavigate();
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [filters, setFilters] = React.useState({ id:'', period:'', object:'', status:'', createdAt:'', signed:'' });
  const [statusQuickFilter, setStatusQuickFilter] = React.useState('');
  const [selectedId, setSelectedId] = React.useState(null);

  const load = React.useCallback(async () => {
    try {
      setLoading(true); setError('');
  const r = await fetchAuth(`${API}/api/tmc-requests`);
      if (!r.ok) throw new Error('load_failed');
      const data = await r.json();
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      setError('Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const newRequest = () => nav('/tmc-requests/new');
  const newWriteOffRequest = () => window.open('/tmc-requests/writeoff/new', '_blank');

  const filteredRequests = React.useMemo(() => {
    const normalized = {
      id: (filters.id || '').toLowerCase(),
      period: (filters.period || '').toLowerCase(),
      object: (filters.object || '').toLowerCase(),
      status: (filters.status || '').toLowerCase(),
      createdAt: (filters.createdAt || '').toLowerCase(),
      signed: (filters.signed || '').toLowerCase(),
    };
    return (items || [])
      .map((r) => {
        const status = getRequestStatus(r);
        const statusText = status.text;
        const periodText = `${monthName(r.month)} ${r.year}`;
        const createdText = r.createdAt ? new Date(r.createdAt).toLocaleString('ru-RU') : '';
        const signedText = r.gdSignedAt
          ? new Date(r.gdSignedAt).toLocaleString('ru-RU')
          : (r.assistantSignedAt
            ? new Date(r.assistantSignedAt).toLocaleString('ru-RU')
            : (r.managerSignedAt ? new Date(r.managerSignedAt).toLocaleString('ru-RU') : ''));
        return { ...r, statusKey: status.key, statusText, periodText, createdText, signedText };
      })
      .filter((r) => (
        (normalized.id === '' || String(r.id).toLowerCase().includes(normalized.id)) &&
        (normalized.period === '' || r.periodText.toLowerCase().includes(normalized.period)) &&
        (normalized.object === '' || `${r.objectName || ''} ${r.cardCode || ''}`.toLowerCase().includes(normalized.object)) &&
        (statusQuickFilter === '' || r.statusKey === statusQuickFilter) &&
        (normalized.status === '' || r.statusText.toLowerCase().includes(normalized.status)) &&
        (normalized.createdAt === '' || r.createdText.toLowerCase().includes(normalized.createdAt)) &&
        (normalized.signed === '' || r.signedText.toLowerCase().includes(normalized.signed))
      ));
  }, [items, filters, statusQuickFilter]);

  const statusCounts = React.useMemo(() => {
    return (items || []).reduce((acc, row) => {
      const status = getRequestStatus(row);
      if (status.key === 'in_progress') acc.inProgress += 1;
      if (status.key === 'awaiting_gd') acc.awaitingGd += 1;
      return acc;
    }, { inProgress: 0, awaitingGd: 0 });
  }, [items]);

  return (
    <div style={{ padding: '32px 0 0 0' }}>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:12 }}>
        <Link to="/">&larr; В меню</Link>
        <h2 style={{ margin: 0 }}>Журнал заявок на ТМЦ</h2>
        <div style={{ marginLeft:'auto', display:'flex', gap:8 }}>
          <button onClick={load} style={{ padding:'6px 12px' }}>Обновить</button>
          <button onClick={newRequest} style={{ padding:'6px 12px', background:'#e7f6d4', border:'1px solid #8bc34a', borderRadius:6 }}>+ Новая заявка</button>
          <button onClick={newWriteOffRequest} style={{ padding:'6px 12px', background:'#fff1d6', border:'1px solid #f59e0b', borderRadius:6 }}>+ Заявка на списание ТМЦ</button>
        </div>
      </div>

      <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:12 }}>
        <button
          onClick={() => setStatusQuickFilter('')}
          style={{
            padding:'6px 12px',
            border:'1px solid',
            borderColor: statusQuickFilter === '' ? '#2563eb' : '#d1d5db',
            borderRadius:999,
            background: statusQuickFilter === '' ? '#dbeafe' : '#fff',
            color: statusQuickFilter === '' ? '#1d4ed8' : '#334155',
            fontWeight:600,
          }}
        >
          Все
        </button>
        <button
          onClick={() => setStatusQuickFilter('in_progress')}
          style={{
            padding:'6px 12px',
            border:'1px solid',
            borderColor: statusQuickFilter === 'in_progress' ? '#0284c7' : '#d1d5db',
            borderRadius:999,
            background: statusQuickFilter === 'in_progress' ? '#e0f2fe' : '#fff',
            color: statusQuickFilter === 'in_progress' ? '#0369a1' : '#334155',
            fontWeight:600,
          }}
        >
          В обработке ({statusCounts.inProgress})
        </button>
        <button
          onClick={() => setStatusQuickFilter('awaiting_gd')}
          style={{
            padding:'6px 12px',
            border:'1px solid',
            borderColor: statusQuickFilter === 'awaiting_gd' ? '#d97706' : '#d1d5db',
            borderRadius:999,
            background: statusQuickFilter === 'awaiting_gd' ? '#fef9c3' : '#fff',
            color: statusQuickFilter === 'awaiting_gd' ? '#b45309' : '#334155',
            fontWeight:600,
          }}
        >
          На утверждении ГД ({statusCounts.awaitingGd})
        </button>
      </div>

      {loading ? <div style={{ textAlign:'center' }}>Загрузка…</div> : error ? <div style={{ color:'#c00', textAlign:'center' }}>{error}</div> : (
        <>
        <div style={{ overflow:'auto', maxHeight: 'calc(100vh - 160px)' }} className="journal-card">
          <table
            style={{ width:'100%', borderCollapse:'separate', borderSpacing:0, border:'1px solid #e5e7eb', boxShadow:'0 1px 10px rgba(0,0,0,0.06)' }}
            className="journal-table"
          >
            <colgroup>
              <col style={{ width: 80 }} />
              <col style={{ width: 160 }} />
              <col />
              <col style={{ width: 120 }} />
              <col style={{ width: 190 }} />
              <col style={{ width: 180 }} />
              <col style={{ width: 100 }} />
            </colgroup>
            <thead>
              <tr style={{ background:'#f5f5f5' }}>
                <th style={{ padding:10, textAlign:'left', position:'sticky', top:0, zIndex:5 }}>ID</th>
                <th style={{ padding:10, textAlign:'left', position:'sticky', top:0, zIndex:5 }}>Период</th>
                <th style={{ padding:10, textAlign:'left', position:'sticky', top:0, zIndex:5 }}>Объект</th>
                <th style={{ padding:10, textAlign:'left', position:'sticky', top:0, zIndex:5 }}>Статус</th>
                <th style={{ padding:10, textAlign:'left', position:'sticky', top:0, zIndex:5 }}>Создана</th>
                <th style={{ padding:10, textAlign:'left', position:'sticky', top:0, zIndex:5 }}>Подписана</th>
                <th style={{ padding:10, textAlign:'left', position:'sticky', top:0, zIndex:5 }}>Действия</th>
              </tr>
              <tr>
                <th style={{ padding:6 }}>
                  <input value={filters.id} onChange={e=>setFilters(f=>({ ...f, id: e.target.value }))} placeholder="Поиск..." style={{ width:'92%', padding:'6px 8px', fontSize:13 }} />
                </th>
                <th style={{ padding:6 }}>
                  <input value={filters.period} onChange={e=>setFilters(f=>({ ...f, period: e.target.value }))} placeholder="мм гггг" style={{ width:'92%', padding:'6px 8px', fontSize:13 }} />
                </th>
                <th style={{ padding:6 }}>
                  <input value={filters.object} onChange={e=>setFilters(f=>({ ...f, object: e.target.value }))} placeholder="Название или код" style={{ width:'96%', padding:'6px 8px', fontSize:13 }} />
                </th>
                <th style={{ padding:6 }}>
                  <input value={filters.status} onChange={e=>setFilters(f=>({ ...f, status: e.target.value }))} placeholder="Черновик/В обработке/На утверждении у ГД/Выполнено" style={{ width:'96%', padding:'6px 8px', fontSize:13 }} />
                </th>
                <th style={{ padding:6 }}>
                  <input value={filters.createdAt} onChange={e=>setFilters(f=>({ ...f, createdAt: e.target.value }))} placeholder="Дата/время" style={{ width:'92%', padding:'6px 8px', fontSize:13 }} />
                </th>
                  <th style={{ padding:6 }}>
                  <input value={filters.signed} onChange={e=>setFilters(f=>({ ...f, signed: e.target.value }))} placeholder="Дата/время" style={{ width:'92%', padding:'6px 8px', fontSize:13 }} />
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredRequests
                .map((r, i) => {
                  const isSelected = selectedId === r.id;
                  const zebraBg = i % 2 === 0 ? '#fff' : '#fafafa';
                  const rowBg = isSelected ? '#dbeafe' : zebraBg;
                  const rowBorder = isSelected ? '#93c5fd' : '#e5e7eb';
                  const rowClass = isSelected ? 'journal-row selected' : 'journal-row';
                  const cell = { padding:10, borderTop:`1px solid ${rowBorder}`, background: rowBg };
                  return (
                <tr
                  key={r.id}
                  onClick={()=>setSelectedId(r.id)}
                  onDoubleClick={()=>window.open(`/tmc-requests/${encodeURIComponent(r.id)}`, '_blank')}
                  className={rowClass}
                  style={{ cursor:'pointer', background: rowBg, transition:'background 0.15s ease', borderLeft:`3px solid ${rowBorder}` }}
                >
                  <td style={{ ...cell }}>
                    <Link to={`/tmc-requests/${encodeURIComponent(r.id)}`} style={linkStyle}>
                      {r.id}
                    </Link>
                  </td>
                  <td style={{ ...cell }}>{r.periodText}</td>
                  <td style={{ ...cell }}>
                    <div style={{ display:'flex', flexDirection:'column' }}>
                      <span>{r.objectName || '—'}</span>
                      <span style={{ color:'#6b7280', fontSize:12 }}>{r.cardCode}</span>
                    </div>
                  </td>
                  <td style={{ ...cell }}><StatusBadge row={r} /></td>
                  <td style={{ ...cell }}>{r.createdText}</td>
                  <td style={{ ...cell }}>{r.signedText || '—'}</td>
                  <td style={{ ...cell }}>
                    {r.statusText === 'Черновик' && (
                      <button
                        title="Удалить черновик"
                        onClick={async (e)=>{
                          e.stopPropagation();
                          const ok = window.confirm(`Удалить заявку №${r.id}? Это действие необратимо.`);
                          if (!ok) return;
                          try {
                            const resp = await fetchAuth(`${API}/api/tmc-requests/${r.id}`, { method:'DELETE' });
                            if (resp.ok) setItems(prev => prev.filter(x => x.id !== r.id));
                            else alert('Не удалось удалить заявку');
                          } catch {}
                        }}
                        style={{ border:'1px solid #ef4444', background:'#fff', color:'#b91c1c', padding:'4px 8px', borderRadius:6 }}
                      >Удалить</button>
                    )}
                  </td>
                </tr>
              );
            })}
              {filteredRequests.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ padding:12, textAlign:'center', color:'#777' }}>Нет заявок</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 10, fontSize: 13, color: '#475569', textAlign: 'right' }}>
          Строк: <strong>{filteredRequests.length}</strong>
        </div>
        </>
      )}
    </div>
  );
}
